import React, { useState, useEffect, useCallback, useReducer } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import { BlobServiceClient } from '@azure/storage-blob'
import Checkbox from '@mui/material/Checkbox'
import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import TextField from '@mui/material/TextField'
import JSZip from 'jszip'
import { uploadFileData } from '../../actions/userActions'
import { closePopup } from '../../actions/uiActions'
import { getDatabaseJobFiles, saveDatabaseJobFile, deleteDatabaseJobFile, purgePreviosJobFiles, dataToolsDeleteBlobsFromUnfinishedJob } from '../../actions/dbFunctionsActions'

function DatabaseJobFileUpload(props) {
    const BLOB_SIZE = 100 * 1024
    const BLOB_BLOCK_SIZE = 4 * 1024 * 1024
    const CONCURRENCY = 3
    const onDrop = useCallback(async acceptedFiles => {
        setIsLoadingRequest(true)
        let files = []
        let allFileNames = []
        let processedFiles = []
        let totalFilesProcessed = 0
        let rejectedFiles = []
        let fileSnips = []

        const jobLookupId = (props.popupInfo.jobData) ? props.popupInfo.jobData.id : -1
        const inBrandNewFileMode = jobLookupId === -1

        if (acceptedFiles.length > 0) {
            if (inBrandNewFileMode) {
                for (let fileItr = 0; fileItr < acceptedFiles.length; fileItr++) {
                    if (isThisFileZipped(acceptedFiles[fileItr]["name"])) {
                        files.push(acceptedFiles[fileItr])
                        allFileNames.push(acceptedFiles[fileItr]["name"].toLowerCase().trim())
                    } else if (isThisFileTypeAccepted(acceptedFiles[fileItr]["name"])) {
                        files.push(acceptedFiles[fileItr])
                        allFileNames.push(acceptedFiles[fileItr]["name"].toLowerCase().trim())
                    } else if (!isThisFileTypeAccepted(acceptedFiles[fileItr]["name"])) {
                        rejectedFiles.push(acceptedFiles[fileItr]["name"])
                    }
                }
            } else {
                if (isThisFileZipped(acceptedFiles[0]["name"])) {
                    files.push(acceptedFiles[0])
                    allFileNames.push(acceptedFiles[0]["name"].toLowerCase().trim())
                } else if (isThisFileTypeAccepted(acceptedFiles[0]["name"])) {
                    files.push(acceptedFiles[0])
                    allFileNames.push(acceptedFiles[0]["name"].toLowerCase().trim())
                } else if (!isThisFileTypeAccepted(acceptedFiles[0]["name"])) {
                    rejectedFiles.push(acceptedFiles[0]["name"])
                }
            }
        }
        
        let lookupExistingFiles = props.popupInfo.existingFiles.filter(f => allFileNames.includes(f.fileName.toLowerCase().trim()) && f.id !== jobLookupId)
        let lookupWithinZips = doesThisFileNameExistsWithin(props.popupInfo.existingFiles, allFileNames)
        let lookupAllUploadedZipFiles = await doesThisZipFileContainOneFileThatWasUploadedIndividually(files, props.popupInfo.existingFiles)
        let lookupAllUploadedZipFilesJustUploadedFiles = await doesThisZipFileContainOneFileThatWasUploadedIndividually(files, files)
        if (lookupExistingFiles.length > 0) {
            files = []
            setErrorMessage(`File [${lookupExistingFiles[0]["fileName"]}] alerady exists`)
            setInvalid(true)
        } else if (lookupWithinZips.length > 0) {
            files = []
            setErrorMessage(`File [${lookupWithinZips[0]}] alerady exists within another zip file`)
            setInvalid(true)
        } else if (lookupAllUploadedZipFiles.length > 0) {
            files = []
            setErrorMessage(`File [${lookupAllUploadedZipFiles[0]}] from zip file is already a global file`)
            setInvalid(true)
        } else if (lookupAllUploadedZipFilesJustUploadedFiles.length > 0) {
            files = []
            setErrorMessage(`File [${lookupAllUploadedZipFilesJustUploadedFiles[0]}] exists in multiple places within the upload`)
            setInvalid(true)
        } else if (!inBrandNewFileMode && !areExtensionsEqual((props.popupInfo.jobData) ? props.popupInfo.jobData.jobFileData : null, JSON.parse(props.popupInfo.jobData.jobFileData).name, allFileNames[0])) {
            files = []
            setErrorMessage(`Files [${allFileNames[0]}] and [${JSON.parse(props.popupInfo.jobData.jobFileData).name}] do not have the same extension`)
            setInvalid(true)
        } else {
            setErrorMessage('')
            setInvalid(false)

            if (rejectedFiles.length > 0) {
                setErrorMessage(`File(s); ${rejectedFiles.join(", ")} are not allowed`)
            }

            if (files.length > 0) {
                setUploadedNewFiles(true)
            }
        }

        if (files.length > 0) {
            let allFileSizesProcessed = 0
            let combinedFileSizes = 0
            for (let fileItr = 0; fileItr < files.length; fileItr++) {
                combinedFileSizes += files[fileItr]["size"]
            }
            setCombinedUploadFileSizes(combinedFileSizes)
            setFileSizeProcessed(allFileSizesProcessed)

            // Go though each DB File Upload
            for (let x = 0; x < files.length; x++) {
                let data = {
                    name: files[x]["name"],
                    size: files[x]["size"]
                }

                let dbUploadFileReq = await props.uploadFileData(data)
                let dbUploadFileResp = await dbUploadFileReq.json()

                // Check to see if the current file we are dealing with is a zip file
                let isZip = false
                if (isThisFileZipped(files[x]["name"])) {
                    isZip = true
                }

                // Peek inside the zip file here and get the files within it
                let fileNames = []
                if (isZip) {
                    let zipCall = await JSZip.loadAsync(files[x])
                    fileNames = Object.keys(zipCall["files"])

                    for (let zipFileItr = 0; zipFileItr < fileNames.length; zipFileItr++) {
                        const currentFile = zipCall.file(fileNames[zipFileItr])
                        try {
                            if (currentFile) {
                                const fileData = await currentFile.async("arraybuffer")
                                const uint8Array = new Uint8Array(fileData)
                                const zipFileSnippet = uint8Array.slice(0, BLOB_SIZE)
                                const textDecoder = new TextDecoder()
                                const fileContent = textDecoder.decode(zipFileSnippet)
                                fileSnips.push({
                                    "fileName": fileNames[zipFileItr],
                                    "rawData": fileContent
                                })
                            }
                            console.log("Finished With File " + fileNames[zipFileItr] + " " + (zipFileItr+1) + "/" + fileNames.length)
                        } catch {
                            console.log("Error with file " + fileNames[zipFileItr] + " using empty snippet for now, " + (zipFileItr+1) + "/" + fileNames.length)
                            fileSnips.push({
                                "fileName": fileNames[zipFileItr],
                                "rawData": ''
                            })
                        }
                        
                    }
                }

                // Start putting in the necessary files
                processedFiles.push({ 
                    "name": data.name, 
                    "url": dbUploadFileResp["url"], 
                    "containerName": dbUploadFileResp["containerName"], 
                    "blobName": dbUploadFileResp["blobName"],
                    "identifier": lookupIfWeNeedNewGuid((props.popupInfo.jobData) ? props.popupInfo.jobData.jobFileData : null, data.name, inBrandNewFileMode),
                    "zipIndividualFiles": fileNames
                })

                if (!isZip) {
                    // Take a peek inside the file
                    const fileReader = new FileReader()
                    const blob = files[x].slice(0, BLOB_SIZE)

                    // Calls for when we load up these small file snippets
                    fileReader.onload = function(e) { 
                        const fileSnippet = e.target.result
                        fileSnips.push({
                            "fileName": data.name,
                            "rawData": fileSnippet
                        })
                    }
                    fileReader.onerror = function(e) { console.error("Error reading file", e) }
                    
                    // We only want to read parts of the file when they can act as data sources
                    if (hasValidExtension(data.name)) {
                        fileReader.readAsText(blob)
                    }
                }
                
                let blobServiceClient = new BlobServiceClient(dbUploadFileResp["url"])
                let containerClient = blobServiceClient.getContainerClient(dbUploadFileResp["containerName"])
                let blockBlobClient = containerClient.getBlockBlobClient(dbUploadFileResp["blobName"])
                let uploadOptions = {
                    blobHTTPHeaders: { blobContentType: files[x]["type"] },
                    blockSize: BLOB_BLOCK_SIZE,
                    concurrency: CONCURRENCY,
                    onProgress: (progress) => {
                        let newUpdateLevel = allFileSizesProcessed + progress["loadedBytes"]
                        setFileSizeProcessed(newUpdateLevel)
                    }
                }

                try {
                    console.log("Uploading File: " + data["name"])
                    await blockBlobClient.uploadData(files[x], uploadOptions)
                    totalFilesProcessed++
                }
                catch (e) {
                    console.error(e)
                }
                finally {
                    allFileSizesProcessed += files[x]["size"]
                    setFileSizeProcessed(allFileSizesProcessed)
                }
            }

            setUploadedFiles(processedFiles)
            setFileSnippets(fileSnips)
        }

        setIsLoadingRequest(false)
    }, [])
    
    const [isLoadingRequest, setIsLoadingRequest] = useState(false)
    const [invalid, setInvalid] = useState(false)
    const [combinedUploadFileSizes, setCombinedUploadFileSizes] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [fileSnippets, setFileSnippets] = useState([])
    const [fileSizeProcessed, setFileSizeProcessed] = useState(0)
    const [filesPresaved, setFilesPresaved] = useState([])
    const [teamIds, setTeamIds] = useState([])
    const [errorMessage, setErrorMessage] = useState('')
    const [description, setDescription] = useState('')
    const [editId, setEditId] = useState(-1)
    const [uploadedNewFiles, setUploadedNewFiles] = useState(false)
    const [header, setHeader] = useState('Upload Job Files')
    const [, forceUpdate] = useReducer(x => x + 1, 0)
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop})

    // trigger on component mount
    useEffect(() => {
        if (props.popupInfo.jobData !== null) {
            setHeader('Edit Files')
            setEditId(props.popupInfo.jobData.id)
            if (props.popupInfo.jobData.fileDescription) {
                setDescription(props.popupInfo.jobData.fileDescription)
            }

            // Set files up, check if Array or singular object
            // TODO: Eventually, we're gonna just get rid of the singular check
            if (Array.isArray(JSON.parse(props.popupInfo.jobData.jobFileData))) {
                let jobData = JSON.parse(props.popupInfo.jobData.jobFileData)
                let result = []
                for (let jobDataItr = 0; jobDataItr < jobData.length; jobDataItr++) {
                    result.push(jobData[jobDataItr])
                    setFilesPresaved(result)
                }
            } else {
                let jobData = JSON.parse(props.popupInfo.jobData.jobFileData)
                if (jobData && jobData.name !== undefined)
                {
                    setFilesPresaved([ jobData ])
                }
            }

            if (props.popupInfo.jobData.databaseJobFileTeams) {
                let copiedOverTeamIds = []
    
                for (let x = 0; x < props.popupInfo.jobData.databaseJobFileTeams.length; x++) {
                    copiedOverTeamIds.push(props.popupInfo.jobData.databaseJobFileTeams[x]["teamID"])
                }
    
                setTeamIds(copiedOverTeamIds)
            }
        }
    }, [])

    function handleClick(event) {
        event.preventDefault()

        if (event.target.className !== 'MuiDialog-container MuiDialog-scrollPaper css-hz1bth-MuiDialog-container' &&
            event.target.className !== "MuiDialog-container MuiDialog-scrollPaper css-ekeie0") {
            let allCurrentlyUploadedBlobs = []
            for (let x = 0; x < uploadedFiles.length; x++) {
                allCurrentlyUploadedBlobs.push(uploadedFiles[x]["blobName"])
            }
            props.dataToolsDeleteBlobsFromUnfinishedJob(allCurrentlyUploadedBlobs)
    
            props.closePopup()
        }
    }

    function handleJobStepsSave(event) {
        event.preventDefault()
        let files = filesPresaved

        if (files.length === 0) {
            files = uploadedFiles
        }

        if (files.length > 0) {
            setIsLoadingRequest(true)

            if (editId > -1 && uploadedNewFiles) {
                props.purgePreviosJobFiles(editId).then((resp) => {
                    if (resp.status !== 200) {
                        setIsLoadingRequest(false)
                        setErrorMessage('Error when purging older files')
                        setInvalid(true)
                    } else {
                        props.saveDatabaseJobFile(editId, uploadedFiles, teamIds, description, fileSnippets).then((resp) => {
                            setIsLoadingRequest(false)
                            props.getDatabaseJobFiles()
                            props.closePopup()
                        })
                    }
                })
            } else {
                props.saveDatabaseJobFile(editId, files, teamIds, description, fileSnippets).then((resp) => {
                    setIsLoadingRequest(false)
                    props.getDatabaseJobFiles()
                    props.closePopup()
                })
            }
        }
    }

    function renderSubmitButton() {
        if (isLoadingRequest) {
            return <button type="submit" className="btn" disabled onClick={() => {}}>Saving...</button>
        }
        if (invalid) {
            return <button type="submit" className="btn" disabled onClick={() => {}}>Invalid File</button>
        }

        if (fileSizeProcessed > 0) {
            return <button type="submit" className="btn" onClick={handleJobStepsSave}>OK</button>
        }
        
        return <button type="submit" className="btn" onClick={handleJobStepsSave}>Upload File</button>
    }

    function renderFileNameLine() {
        if (uploadedFiles.length === 0) {
            if (filesPresaved.length === 0) {
                if (isLoadingRequest) {
                    return <p>Uploading...</p>
                } else {
                    return <p>Drag 'n' drop some files here, or click to select files</p>
                }
            } else {
                return <>
                    {filesPresaved.map((file,fIndex) => <p key={`file-${fIndex}`}>{file.name}</p> )}
                </>
            }
        } else {
            return <>
                {uploadedFiles.map(file => <p key={`file-${file.lastModified}`}>{file.name}</p> )}
            </>
        }
    }

    function renderProgressBar() {
        if (isLoadingRequest && combinedUploadFileSizes > 0) {
            return (<div className="progressBar" style={{ "width": "100%" }}><div className="filler" style={{ width: `${(fileSizeProcessed/combinedUploadFileSizes) * 100}%` }}></div></div>)
        }
        
        return null
    }

    function renderTeamsCheckboxes() {
        let teams = props.teamList.filter(x => x.customer.id === parseInt(props.currentUser.customerID)).sort((a, b) => parseFloat(a.id) - parseFloat(b.id))

        if (props.role === "Builder") {
            let filteredTeams = []
            let allTeamIds = []

            for (let allTeamIndex = 0; allTeamIndex< teams.length; allTeamIndex++) {
                for (let userTeamIndex = 0; userTeamIndex < props.currentUser.applicationUserTeams.length; userTeamIndex++) {
                    if (teams[allTeamIndex]["id"] === props.currentUser.applicationUserTeams[userTeamIndex]["teamID"]) {
                        filteredTeams.push(teams[allTeamIndex])
                        allTeamIds.push(props.currentUser.applicationUserTeams[userTeamIndex]["teamID"])
                    }
                }
            }

            teams = filteredTeams

            if (teamIds.length === 0) {
                setTeamIds(allTeamIds)
            }
        }

        if (teams.length > 0) {
            return (<div className="field">
                <FormLabel component="legend">This file will be available to the following teams</FormLabel>
                {teams.map((x,i) => (
                    <FormControlLabel
                        key={`team-${i}`}
                        label={x.name}
                        control={
                            <Checkbox
                                checked={teamIds.includes(x.id)}
                                onChange={(e) => {
                                    e.preventDefault()
                                    let copiedTeamIds = teamIds
                                
                                    if (e.target.checked) {
                                        copiedTeamIds.push(x.id)
                                    } else {
                                        copiedTeamIds = removeItemOnce(copiedTeamIds, x.id)
                                    }
                                
                                    setTeamIds(copiedTeamIds)
                                    forceUpdate()
                                }
                            }/>}>
                    </FormControlLabel>
                ))}
            </div>)
        }
        return null
    }

    /**
     * Are we currently accepting/processing this file type
     * @param {string} fileType - File Type To Look Up
     */
    function isThisFileTypeAccepted(fileName) {
        let fileExtension = getFileExtension(fileName)
        switch (fileExtension) {
            case 'txt':
            case 'json':
            case 'xls':
            case 'xlsx':
            case 'xdb':
            case 'accdb':
            case 'mdb':
            case 'csv':
            case 'xmp':
            case 'xdb':
            case 'xpi':
                return true
            default: return false
        }
    }

    /** 
     * isThisFileZipped
     * * Uses a previously written method to determine if this is a zip file
     * @param {string} fileName - Name of the file with extension
    */
    function isThisFileZipped(fileName) {
        return getFileExtension(fileName) === 'zip'
    }
    
    function removeItemOnce(arr, value) {
        var index = arr.indexOf(value);
        if (index > -1) {
            arr.splice(index, 1);
        }
        return arr;
    }

    function generateGUID() {
        return 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0,
              v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
    }

    /**
     * lookupIfWeNeedNewGuid
     * * Multiple scenarios to check if we are generating a new GUID or using an existing one
     * @param {object} jobFileData 
     * @param {string} name 
     * @param {boolean} newFileMode 
     * @returns {string} GUID String
     */
    function lookupIfWeNeedNewGuid(jobFileData, name, newFileMode) {
        if (jobFileData === null) {
            return generateGUID()
        } else if (newFileMode) {
            return generateGUID()   
        }

        let jobData = JSON.parse(jobFileData)
        if (jobData && jobData.identifier !== undefined) {
            return jobData.identifier
        }

        return generateGUID()
    }

    /**
     * getFileExtension
     * * Get get file extension from string
     * @param {string} fileName - File Name
     * @returns string of the extension
     */
    function getFileExtension(fileName) {
        return fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2);
    }
    
    /**
     * areExtensionsEqual
     * * Do the two files have the same extention
     * @param {object} jobFileData - File Data passed if we are in edit mode
     * @param {string} fileName1 - File 1
     * @param {string} fileName2 - File 2
     * @returns boolean, true if file extensions from both strings are the same
     */
    function areExtensionsEqual(jobFileData, fileName1, fileName2) {
        if (jobFileData === null) {
            return
        }

        return getFileExtension(fileName1) === getFileExtension(fileName2);
    }

    /**
     * doesThisFileNameExistsWithin
     * * Look at the current file name, check within all other zip files to determine if a file with a matching name is already there
     * @param {Array} existingFiles - Existing files from the props
     * @param {string[]} allFileNames - An array of strings, all file names lower cased
     * @returns Array, all file names that it caught
    */
    function doesThisFileNameExistsWithin(existingFiles, allFileNames) {
        let rtnVal = []
        for (let e = 0; e < existingFiles.length; e++) {
            let jobFileData = JSON.parse(existingFiles[e]["jobFileData"])
            if (jobFileData && jobFileData["zipIndividualFiles"] && jobFileData["zipIndividualFiles"].length > 0) {
                let zipIndividualFiles = jobFileData["zipIndividualFiles"]
                let lookup = zipIndividualFiles.filter(z => allFileNames.includes(z.toLowerCase()))
                for (let x = 0; x < lookup.length; x++) {
                    rtnVal.push(lookup[x])
                }
            }
        }
        return rtnVal
    }

    function hasValidExtension(filename) {
        return /\.(csv|txt)$/i.test(filename)
    }

    /**
     * doesThisZipFileContainOneFileThatWasUploadedIndividually
     * * Look at each zip file that was uploaded, check if it contains a file that matches what was already uploaded
     * @param {Array} uploadedFiles - File data captured from the dropzone control
     * @param {Array} existingFiles - Existing files from the props
     * @returns Array, all file names that it caught
    */
    async function doesThisZipFileContainOneFileThatWasUploadedIndividually(uploadedFiles, existingFiles) {
        let rtnVal = []
        let allFileNames = []
        let allFileNamesWithinZip = []

        // Fill in [allFileNames] from existing files, just check the file names
        for (let fileItr = 0; fileItr < existingFiles.length; fileItr++) {
            if (existingFiles[fileItr]["fileName"]) {
                allFileNames.push(existingFiles[fileItr]["fileName"].toLowerCase().trim())
            } else if (existingFiles[fileItr]["name"]) {
                allFileNames.push(existingFiles[fileItr]["name"].toLowerCase().trim())
            }
        }

        // Look inside each zip file, get the file name of each zip file, populate them with [allFileNamesWithinZip]
        for (let uploadedFileItr = 0; uploadedFileItr < uploadedFiles.length; uploadedFileItr++) {
            // Check to see if the current file we are dealing with is a zip file
            let isZip = false
            if (isThisFileZipped(uploadedFiles[uploadedFileItr]["name"])) {
                isZip = true
            }

            // Peek inside the zip file here and get the files within it
            let fileNames = []
            if (isZip) {
                let zipCall = await JSZip.loadAsync(uploadedFiles[uploadedFileItr])
                fileNames = Object.keys(zipCall["files"])
                for (let x = 0; x < fileNames.length; x++) {
                    allFileNamesWithinZip.push(fileNames[x].toLowerCase().trim())
                }
            }
        }

        let lookup = allFileNamesWithinZip.filter(z => allFileNames.includes(z.toLowerCase()))
        for (let x = 0; x < lookup.length; x++) {
            rtnVal.push(lookup[x])
        }

        return rtnVal
    }

    // This is the render logic, no need to declare another method is a functional component
    return <Dialog
        maxWidth={"lg"}
        fullWidth={true}
        open={true}
        fullScreen={false}
        onClose={handleClick}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
    >
        <CloseIcon onClick={handleClick} className="closePoup" />
        <DialogTitle id="alert-dialog-title" className="changepassdialog">
            {header}
            {' '}
        </DialogTitle>
        <DialogContent>
            <form className="form dbFunctions">
                <div {...getRootProps()} className="field">
                    <input {...getInputProps()} />
                    { isDragActive ? <p>Drop the files here ...</p> : renderFileNameLine() }
                </div>
                <div className="field">
                    <TextField
                        fullWidth
                        label="File Description"
                        variant="outlined"
                        value={description}
                        disabled={false}
                        onChange={(e) => { e.preventDefault(); setDescription(e.target.value); }} />
                </div>
                <div className="field">{errorMessage}</div>
                {renderTeamsCheckboxes()}
                <div className="field">
                    {renderProgressBar()}
                </div>
                <div className="field">
                    {renderSubmitButton()}
                    <button style={{ "marginLeft" : "5px" }} className="btn" onClick={handleClick}>Cancel</button>
                </div>
            </form>
        </DialogContent>
    </Dialog>
}

function mapStateToProps(state) {
    return {
        popupInfo: state.ui.popupInfo,
        role: state.users.role,
        teamList: state.users.teamList,
        currentUser: state.users.currentUser
    }
}

function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators({ closePopup, uploadFileData, getDatabaseJobFiles, saveDatabaseJobFile, deleteDatabaseJobFile, purgePreviosJobFiles, dataToolsDeleteBlobsFromUnfinishedJob }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DatabaseJobFileUpload)